/**
 * External dependencies
 */
import { findKey } from 'lodash';
/**
 * WordPress dependencies
 */

import { useRef } from '@wordpress/element';
import { useRefEffect } from '@wordpress/compose';
import { insert, toHTMLString } from '@wordpress/rich-text';
import { getBlockTransforms, findTransform } from '@wordpress/blocks';
import { useDispatch } from '@wordpress/data';
/**
 * Internal dependencies
 */

import { store as blockEditorStore } from '../../store';
import { preventEventDiscovery } from './prevent-event-discovery'; // A robust way to retain selection position through various
// transforms is to insert a special character at the position and
// then recover it.

const START_OF_SELECTED_AREA = '\u0086';

function findSelection(blocks) {
  let i = blocks.length;

  while (i--) {
    const attributeKey = findKey(blocks[i].attributes, v => typeof v === 'string' && v.indexOf(START_OF_SELECTED_AREA) !== -1);

    if (attributeKey) {
      blocks[i].attributes[attributeKey] = blocks[i].attributes[attributeKey].replace(START_OF_SELECTED_AREA, '');
      return blocks[i].clientId;
    }

    const nestedSelection = findSelection(blocks[i].innerBlocks);

    if (nestedSelection) {
      return nestedSelection;
    }
  }
}

export function useInputRules(props) {
  const {
    __unstableMarkLastChangeAsPersistent,
    __unstableMarkAutomaticChange
  } = useDispatch(blockEditorStore);
  const propsRef = useRef(props);
  propsRef.current = props;
  return useRefEffect(element => {
    function inputRule() {
      const {
        value,
        onReplace,
        selectionChange
      } = propsRef.current;

      if (!onReplace) {
        return;
      }

      const {
        start,
        text
      } = value;
      const characterBefore = text.slice(start - 1, start); // The character right before the caret must be a plain space.

      if (characterBefore !== ' ') {
        return;
      }

      const trimmedTextBefore = text.slice(0, start).trim();
      const prefixTransforms = getBlockTransforms('from').filter(_ref => {
        let {
          type
        } = _ref;
        return type === 'prefix';
      });
      const transformation = findTransform(prefixTransforms, _ref2 => {
        let {
          prefix
        } = _ref2;
        return trimmedTextBefore === prefix;
      });

      if (!transformation) {
        return;
      }

      const content = toHTMLString({
        value: insert(value, START_OF_SELECTED_AREA, 0, start)
      });
      const block = transformation.transform(content);
      selectionChange(findSelection([block]));
      onReplace([block]);

      __unstableMarkAutomaticChange();
    }

    function onInput(event) {
      const {
        inputType,
        type
      } = event;
      const {
        value,
        onChange,
        __unstableAllowPrefixTransformations,
        formatTypes
      } = propsRef.current; // Only run input rules when inserting text.

      if (inputType !== 'insertText' && type !== 'compositionend') {
        return;
      }

      if (__unstableAllowPrefixTransformations && inputRule) {
        inputRule();
      }

      const transformed = formatTypes.reduce((accumlator, _ref3) => {
        let {
          __unstableInputRule
        } = _ref3;

        if (__unstableInputRule) {
          accumlator = __unstableInputRule(accumlator);
        }

        return accumlator;
      }, preventEventDiscovery(value));

      if (transformed !== value) {
        __unstableMarkLastChangeAsPersistent();

        onChange({ ...transformed,
          activeFormats: value.activeFormats
        });

        __unstableMarkAutomaticChange();
      }
    }

    element.addEventListener('input', onInput);
    element.addEventListener('compositionend', onInput);
    return () => {
      element.removeEventListener('input', onInput);
      element.removeEventListener('compositionend', onInput);
    };
  }, []);
}
//# sourceMappingURL=use-input-rules.js.map